/*  ========================================================================
    # Hooks - Use Posts API
    ========================================================================  */

import { useEffect, useState } from 'react';
import type { Post } from '@/react/types/post';

export type ResponsePost = {
  id: number;
  date: string;
  date_gmt: string;
  modified: string;
  modified_gmt: string;
  slug: string;
  status: string;
  type: string;
  link: string;
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
    protected: boolean;
  };
  excerpt: {
    rendered: string;
    protected: boolean;
  };
  author: number;
  comment_status: string;
  sticky: boolean;
  categories: number[];
  featured_image: {
    thumbnail: string;
    'thumbnail-width': number;
    'thumbnail-height': number;
    medium: string;
    'medium-width': number;
    'medium-height': number;
    medium_large: string;
    'medium_large-width': number;
    'medium_large-height': number;
    large: string;
    'large-width': number;
    'large-height': number;
    '1536x1536': string;
    '1536x1536-width': number;
    '1536x1536-height': number;
    '2048x2048': string;
    '2048x2048-width': number;
    '2048x2048-height': number;
    placeholder: string;
    'placeholder-width': number;
    'placeholder-height': number;
  };
  _embedded: {
    'wp:term': {
      id: number;
      link: string;
      name: string;
      slug: string;
      taxonomy: string;
    }[][];
  };
};

const usePostsApi = (params: {
  per_page: number;
  page: number;
  categories: number[];
  search: string;
  categories_exclude: number[];
}) => {
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [postsCount, setPostsCount] = useState(0);
  const [posts, setPosts] = useState<Post[]>([]);
  const paramsString = new URLSearchParams();

  const getData = async () => {
    setLoadingPosts(true);

    for (const [key, value] of Object.entries(params)) {
      if (key === 'categories' || key === 'categories_exclude') {
        if ((value as number[]).length > 0) paramsString.append(key, value as string);
      } else {
        paramsString.append(key, value as string);
      }
    }

    try {
      const response = await fetch(
        `/wp-json/wp/v2/posts?_embed=wp:term&${paramsString.toString()}`
      );

      if (response.status !== 200) throw new Error(await response.text());

      const posts: ResponsePost[] = await response.json();
      const mappedPosts: Post[] = posts.map(post => ({
        permalink: post.link,
        category: post._embedded['wp:term'][0]?.[0]?.name
          ? post._embedded['wp:term'][0]?.[0]?.name
          : '',
        date: post.date,
        title: post.title.rendered,
        excerpt: post.excerpt.rendered,
        image: {
          sizes: post.featured_image,
          alt: post.title.rendered
        }
      }));

      setLoadingPosts(false);
      setPostsCount(parseInt(response.headers.get('X-WP-Total') || '0', 10));
      setPosts(mappedPosts);
    } catch (error) {
      setLoadingPosts(false);
      console.log(error); // eslint-disable-line
    }
  };

  useEffect(() => {
    getData();
  }, [params.per_page, params.page, params.categories, params.search]);

  return { loadingPosts, postsCount, posts };
};

export default usePostsApi;
